import { Chain } from '@1delta/chain-registry'
import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import { annualizeRateDeltaPercent } from '../annualizeRate'

/**
 * Core chain (1116) liquid staking intrinsic yields. Three CORE LSTs,
 * three different APR sources — all resilient (a failure in one leaves
 * its key absent, defaulting `supplyRate` to 0 downstream):
 *
 *  - **stCORE** (`Liquid staked CORE::stCORE`) — computed fully on-chain
 *    from the `Earn` contract's stored daily exchange-rate history
 *    (`getExchangeRates(n)`); no archival node or API needed.
 *  - **dualCORE** (`b14g dualCORE::DUALCORE`) — from DefiLlama's
 *    per-pool chart (b14g auto-compounds; no on-chain rate history).
 *  - **SCORE** (`StakedCore::SCORE`) — rate-delta of
 *    `totalStaked()/totalSupply()` over a historical window (needs an
 *    archival Core RPC; falls back to 0 when none serves old state).
 */

const STCORE_KEY = 'Liquid staked CORE::stCORE'
const DUALCORE_KEY = 'b14g dualCORE::DUALCORE'
const SCORE_KEY = 'StakedCore::SCORE'

const ONE_E18 = 10n ** 18n

/** stCORE `Earn` contract (`stCORE.earn()`). */
const EARN: Address = '0xf5fa1728babc3f8d2a617397fac2696c958c3409'
/** SCORE (StakedCore) — its own staking contract. */
const SCORE: Address = '0xa20b3b97df3a02f9185175760300a06b4e0a2c05'

const GET_EXCHANGE_RATES_ABI = [
  {
    name: 'getExchangeRates',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'count' }],
    outputs: [{ type: 'uint256[]' }],
  },
] as const

const VIEW_U256 = (name: string) =>
  [
    {
      name,
      type: 'function',
      stateMutability: 'view',
      inputs: [],
      outputs: [{ type: 'uint256' }],
    },
  ] as const

/**
 * stCORE APR from the Earn contract's on-chain rate history. Core
 * advances one staking round per day, so `getExchangeRates(n)` returns
 * `n` consecutive daily CORE-per-stCORE rates (oldest→newest); the rate
 * scale (1e6) cancels in the ratio. Simple (non-compounded)
 * annualisation over the sampled window.
 */
const computeStCoreApr = async (): Promise<number> => {
  for (let rpcId = 0; rpcId < 4; rpcId++) {
    try {
      const client = getEvmClient(Chain.CORE_BLOCKCHAIN_MAINNET, rpcId)
      const rates = (await client.readContract({
        address: EARN,
        abi: GET_EXCHANGE_RATES_ABI,
        functionName: 'getExchangeRates',
        args: [8n],
      })) as readonly bigint[]
      if (!rates || rates.length < 2) continue
      const first = rates[0]
      const last = rates[rates.length - 1]
      const days = rates.length - 1
      if (first <= 0n || last < first) continue
      const apr = annualizeRateDeltaPercent(last, first, days * 86_400)
      if (Number.isFinite(apr) && apr >= 0) return apr
    } catch {
      // try next RPC
    }
  }
  throw new Error('stCORE APR: Earn.getExchangeRates unavailable')
}

/** dualCORE APY from DefiLlama's per-pool chart (b14g CORE vault). */
const DUALCORE_LLAMA_POOL = 'f183f1a4-5d17-4bfa-8270-32d861a13fd3'
const fetchDualCoreApy = async (): Promise<number> => {
  const res = await fetch(
    `https://yields.llama.fi/chart/${DUALCORE_LLAMA_POOL}`,
  ).then((r) => r.json())
  const series: any[] = res?.data ?? []
  const latest = series[series.length - 1]
  const apy = Number(latest?.apy ?? latest?.apyBase)
  if (!Number.isFinite(apy) || apy < 0)
    throw new Error('dualCORE APY: bad DefiLlama value')
  return apy
}

/**
 * SCORE APR via rate-delta. SCORE = `totalStaked()/totalSupply()`;
 * sampled now vs a historical block and annualised. Needs an archival
 * Core RPC for the old read — tries a few windows/RPCs, returns 0 if
 * none serves the historical state.
 */
const computeScoreApr = async (): Promise<number> => {
  const WINDOWS = [604_800n, 259_200n, 86_400n] // ~7d / 3d / 1d at ~1s blocks
  const rateAt = async (client: any, block?: bigint): Promise<number> => {
    const opt = block !== undefined ? { blockNumber: block } : {}
    const [staked, supply] = await Promise.all([
      client.readContract({
        address: SCORE,
        abi: VIEW_U256('totalStaked'),
        functionName: 'totalStaked',
        ...opt,
      }),
      client.readContract({
        address: SCORE,
        abi: VIEW_U256('totalSupply'),
        functionName: 'totalSupply',
        ...opt,
      }),
    ])
    const sup = supply as bigint
    if (sup <= 0n) return 0
    return Number(((staked as bigint) * ONE_E18) / sup) / 1e18
  }
  for (const window of WINDOWS) {
    for (let rpcId = 0; rpcId < 3; rpcId++) {
      try {
        const client = getEvmClient(Chain.CORE_BLOCKCHAIN_MAINNET, rpcId)
        const head = await client.getBlockNumber()
        if (head <= window) continue
        const oldBlock = head - window
        const [rNow, rOld, bNow, bOld] = await Promise.all([
          rateAt(client),
          rateAt(client, oldBlock),
          client.getBlock({ blockNumber: head }),
          client.getBlock({ blockNumber: oldBlock }),
        ])
        const elapsed = Number(bNow.timestamp - bOld.timestamp)
        if (rOld <= 0 || rNow <= rOld || elapsed <= 0) continue
        const apr = annualizeRateDeltaPercent(rNow, rOld, elapsed)
        if (Number.isFinite(apr) && apr > 0) return apr
      } catch {
        // pruned node / error — next window or RPC
      }
    }
  }
  return 0
}

export const coreLstFetcher: YieldFetcher = {
  label: 'CORE_LST',
  fetch: async () => {
    const [stcore, dualcore, score] = await Promise.allSettled([
      computeStCoreApr(),
      fetchDualCoreApy(),
      computeScoreApr(),
    ])
    const result: Record<string, number> = {}
    if (stcore.status === 'fulfilled') result[STCORE_KEY] = stcore.value
    if (dualcore.status === 'fulfilled') result[DUALCORE_KEY] = dualcore.value
    if (score.status === 'fulfilled' && score.value > 0)
      result[SCORE_KEY] = score.value
    return result
  },
}
